import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createPost, getCommunities } from '../lib/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { PenLine, ArrowLeft, Send, Eye, Image, Video, Mic, FileText, File, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

export default function CreatePostPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [communitySlug, setCommunitySlug] = useState('');
  const [flair, setFlair] = useState('');
  const [tags, setTags] = useState('');
  const [communities, setCommunities] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getCommunities({ limit: 50 }).then(res => {
      setCommunities(res.data.communities || []);
    }).catch(console.error);
  }, []);

  if (!user) {
    return (
      <div className="mx-auto max-w-4xl px-3 sm:px-4 lg:px-6 py-12 text-center">
        <h2 className="font-heading text-xl font-bold">Connexion requise</h2>
        <p className="text-sm text-muted-foreground mt-2">Connectez-vous pour publier un post.</p>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !communitySlug) {
      toast.error('Le titre et la communauté sont requis');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createPost({
        title: title.trim(),
        body: body.trim(),
        community_slug: communitySlug,
        flair: flair || null,
        tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      });
      toast.success('Post publié !');
      navigate(`/post/${res.data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la publication');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCommunity = communities.find(c => c.slug === communitySlug);

  const mediaTypes = [
    { icon: Image, label: 'Image' },
    { icon: Video, label: 'Vidéo' },
    { icon: Mic, label: 'Podcast/Audio' },
    { icon: FileText, label: 'PDF' },
    { icon: File, label: 'Fichier' },
  ];

  return (
    <div className="mx-auto max-w-5xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      <Link to="/" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors duration-150">
        <ArrowLeft className="w-4 h-4" /> Retour
      </Link>

      <h1 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
        <PenLine className="w-6 h-6 text-[hsl(var(--primary))]" />
        Créer un post
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
        {/* Form */}
        <Card className="p-6" data-testid="create-post-form">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Community */}
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Communauté <span className="text-[hsl(var(--destructive))]">*</span></label>
              <Select value={communitySlug} onValueChange={setCommunitySlug}>
                <SelectTrigger data-testid="community-select">
                  <SelectValue placeholder="Choisir une communauté..." />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {communities.map(c => (
                    <SelectItem key={c.slug} value={c.slug}>s@{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Titre</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, 300))}
                placeholder="Titre"
                maxLength={300}
                required
                data-testid="post-title-input"
              />
              <p className="text-xs text-muted-foreground mt-1">{title.length}/300</p>
            </div>

            {/* Content */}
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Contenu</label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Contenu"
                className="min-h-[180px]"
                data-testid="post-body-textarea"
              />
            </div>

            {/* Media types */}
            <div>
              <label className="text-sm font-semibold mb-1.5 block">
                Médias <span className="text-xs font-normal text-muted-foreground">(image, vidéo, podcast, PDF, fichier)</span>
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {mediaTypes.map(mt => (
                  <Button key={mt.label} type="button" variant="outline" size="sm" className="gap-1.5 text-xs">
                    <mt.icon className="w-3.5 h-3.5" /> {mt.label}
                  </Button>
                ))}
              </div>
              <div className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-[hsl(var(--primary))]/50 transition-colors cursor-pointer">
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                  <p className="text-sm">Cliquez pour ajouter un média</p>
                </div>
              </div>
            </div>

            {/* Flair */}
            {selectedCommunity && selectedCommunity.flairs && selectedCommunity.flairs.length > 0 && (
              <div>
                <label className="text-sm font-semibold mb-1.5 block">Flair</label>
                <Select value={flair} onValueChange={setFlair}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un flair" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedCommunity.flairs.map(f => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Tags */}
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Tags</label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="tag1, tag2, tag3"
                data-testid="post-tags-input"
              />
            </div>

            {/* Author */}
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Nom d'auteur</label>
              <Input
                value={user.username}
                disabled
                className="bg-[hsl(var(--muted))]"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Annuler</Button>
              <Button type="submit" disabled={submitting || !title.trim() || !communitySlug} className="gap-1.5" data-testid="submit-post-button">
                <Send className="w-4 h-4" />
                {submitting ? 'Publication...' : 'Publier'}
              </Button>
            </div>
          </form>
        </Card>

        {/* Live Preview */}
        <div className="hidden lg:block">
          <Card className="p-4 sticky top-20" data-testid="post-preview">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-4 h-4 text-[hsl(var(--primary))]" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Aperçu</h3>
            </div>
            {(!title && !body) ? (
              <p className="text-sm text-muted-foreground italic">Commencez à écrire pour voir l'aperçu...</p>
            ) : (
              <div>
                {communitySlug && (
                  <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-1">s@{communitySlug}</p>
                )}
                {flair && <Badge variant="outline" className="text-[10px] mb-2">{flair}</Badge>}
                <h3 className="font-heading font-bold text-base mb-2">{title || 'Sans titre'}</h3>
                <div className="prose-medical text-sm leading-relaxed text-muted-foreground">
                  <ReactMarkdown>{body || ''}</ReactMarkdown>
                </div>
                {tags && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {tags.split(',').map((t, i) => t.trim() && (
                      <Badge key={i} variant="outline" className="text-[10px]">{t.trim()}</Badge>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground mt-3">Par {user.username}</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
